import { themeGet } from '@styled-system/theme-get'
import { Badge, Box, Card, Flex, Image, Link, Relative, Text, Truncate } from 'pcln-design-system'
import { User } from 'pcln-icons'
import React from 'react'
import styled from 'styled-components'

const NoWrapText = styled(Text)`
  ${themeGet('mediaQueries.lg')} {
    white-space: nowrap;
  }
`
const NoWrapDateText = styled(Text)`
  ${themeGet('mediaQueries.md')} {
    white-space: nowrap;
  }
`

const StyledFlex = styled(Flex)`
  gap: ${themeGet('space.2')};
`

const StyledWrapper = styled(Box)`
  display: grid;
  row-gap: ${themeGet('space.1')};
`

const StyledImage = styled(Image)`
  object-fit: cover;
  min-width: 125px;
`

const LinkOverlay = styled(Link)`
  position: absolute;
  inset: 0 0 0 0;
  z-index: 1;
  &:hover + ${Card} {
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`

const DateBox = ({ title, date, isSmallerView }) => {
  return (
    <Flex
      flexDirection='column'
      bg='background.light'
      justifyContent='center'
      textAlign='center'
      borderRadius='xl'
      p={[3, null, 2, null, 3]}
      width={[1, null, 'auto']}
      maxWidth={isSmallerView ? [null, null, '135px'] : [null, null, '165px']}
      minWidth={isSmallerView ? [null, null, null, null, 135] : null}
    >
      <NoWrapDateText fontSize={0} color='text.light' bold>
        {title}
      </NoWrapDateText>
      <Text
        textStyle={['heading6', null, 'captionBold', null, isSmallerView ? 'captionBold' : 'heading6']}
        fontSize={1}
      >
        {date}
      </Text>
    </Flex>
  )
}

const RecentActivityRentalCar = ({
  headline,
  subheadline,
  brandImg,
  expressDealText,
  pickUpDate,
  dropOffDate,
  numAdults,
  linkUrl,
  ariaLabel,
  onClick,
  buttonText,
  isSmallerView,
  numReviews,
  overallRating,
  imageSrc,
  altText,
  rcDealType,
  specifyOffAirport,
  withSibling,
}) => {
  const expressLogoImageSrc = `https://s1.pclncdn.com/design-assets/home/recent-activity/express-deals-logo-refresh.png?opto&auto=avif,webp`

  return (
    <Relative>
      <LinkOverlay href={linkUrl} target='_blank' role='link' aria-label={ariaLabel} onClick={onClick} />
      <Card bg='background.lightest' borderRadius='xl' boxShadowSize='md' p={3} borderWidth={0}>
        <StyledFlex flexDirection={['column', null, 'row']} justifyContent='space-between'>
          <StyledFlex>
            <Flex flexDirection='column'>
              <StyledImage
                width={isSmallerView ? 125 : [125, null, null, null, 170]}
                height={[78, null, null, '100%']}
                borderRadius='xl'
                src={imageSrc}
                alt={altText}
                mt={rcDealType == 'EXPRESS' ? '9px' : null}
                mb={rcDealType == 'EXPRESS' ? '14px' : null}
              />
              {brandImg && <Image width={50} margin='auto' src={brandImg} />}
            </Flex>
            <StyledWrapper my='auto'>
              {rcDealType == 'EXPRESS' && (
                <Image
                  width={112}
                  height={16}
                  src={`${expressLogoImageSrc}?opto&auto=avif,webp`}
                  alt='expressLogo'
                />
              )}
              <Truncate
                textStyle={['heading5', null, null, null, isSmallerView ? null : 'heading4']}
                fontSize={[2, null, null, null, 3]}
              >
                {headline}
              </Truncate>
              {expressDealText && (
                <Text textStyle='caption' lineHeight={2}>
                  {expressDealText}
                </Text>
              )}
              <Text textStyle='caption' color='text.light' lineHeight={2} maxWidth='245px'>
                {subheadline}
                {specifyOffAirport && 'Pick-up off-airport'}
              </Text>
              {overallRating && numReviews && (
                <StyledFlex>
                  <Badge color='alert' size='small' my='auto'>
                    {overallRating}
                  </Badge>
                  <Text fontSize={0} my='auto'>{`${numReviews} Ratings`}</Text>
                </StyledFlex>
              )}
            </StyledWrapper>
          </StyledFlex>
          <StyledFlex
            flexDirection={
              withSibling
                ? ['column', null, null, null, null, 'row']
                : ['column', null, 'row']
            }
          >
            <StyledFlex>
              <DateBox title='PICK UP' date={pickUpDate} isSmallerView={isSmallerView} />
              <DateBox title='DROP OFF' date={dropOffDate} isSmallerView={isSmallerView} />
            </StyledFlex>
            <StyledFlex flexDirection={['column', null, null, null, null, isSmallerView ? 'column' : 'row']}>
              <Flex
                flexDirection='column'
                justifyContent='center'
                alignItems='center'
                bg='background.light'
                borderRadius='xl'
                py={[2, null, null, null, 1]}
                px={[3, null, 2, 3, 4, 3]}
                minWidth={isSmallerView ? null : [null, null, null, null, 140]}
              >
                <User size={20} color='text.light' />
                <NoWrapText textStyle='caption' textAlign='center'>{`${numAdults} Adult${
                  numAdults > 1 ? 's' : ''
                }`}</NoWrapText>
              </Flex>
              <Link
                href={linkUrl}
                target='_blank'
                variation='fill'
                size={['medium', null, 'small', null, null, isSmallerView ? 'small' : 'large']}
                my='auto'
                aria-label={ariaLabel}
                onClick={onClick}
                color='secondary'
                style={{ minWidth: '200px', borderRadius: '24px' }}
              >
                {buttonText || 'Continue Booking'}
              </Link>
            </StyledFlex>
          </StyledFlex>
        </StyledFlex>
      </Card>
    </Relative>
  )
}

export default RecentActivityRentalCar
